import { UserRole } from '../../types/user';

/**
 * Permission vocabulary — vertical-agnostic.
 *
 * The set of permission identifiers is shared across all organization types.
 * The actual role → permission *assignments* are NOT here anymore: each vertical
 * owns its own matrix (features/club/permissions, features/school/permissions),
 * resolved by organization type in features/shared/permissions.
 */

// Define all possible permissions in the system
export type Permission =
  // Booking permissions
  | 'BOOKINGS:CREATE'
  | 'BOOKINGS:READ'
  | 'BOOKINGS:UPDATE'
  | 'BOOKINGS:DELETE'
  | 'BOOKINGS:CANCEL_OTHERS'
  | 'BOOKINGS:DISPATCH_OVERRIDE'

  // Aircraft permissions
  | 'AIRCRAFT:CREATE'
  | 'AIRCRAFT:READ'
  | 'AIRCRAFT:UPDATE'
  | 'AIRCRAFT:UPDATE_HOURS'
  | 'AIRCRAFT:DELETE'

  // Member management permissions
  | 'MEMBERS:CREATE'
  | 'MEMBERS:READ'
  | 'MEMBERS:UPDATE'
  | 'MEMBERS:DELETE'
  | 'MEMBERS:ASSIGN_ROLES'

  // Maintenance permissions
  | 'MAINTENANCE:READ'
  | 'MAINTENANCE:CREATE_SQUAWK'
  | 'MAINTENANCE:UPDATE_SQUAWK'
  | 'MAINTENANCE:CLOSE_SQUAWK'
  | 'MAINTENANCE:APPROVE_WORK'

  // Flight planning permissions
  | 'FLIGHT_PLANS:CREATE'
  | 'FLIGHT_PLANS:READ'
  | 'FLIGHT_PLANS:UPDATE'
  | 'FLIGHT_PLANS:DELETE'
  | 'FLIGHT_PLANS:APPROVE'
  | 'FLIGHT_PLANS:DISPATCH_OVERRIDE'

  // Student-CFI assignment permissions
  | 'ASSIGNMENTS:CREATE'
  | 'ASSIGNMENTS:READ'
  | 'ASSIGNMENTS:UPDATE'
  | 'ASSIGNMENTS:DELETE'

  // Training progress permissions
  | 'TRAINING:READ'
  | 'TRAINING:UPDATE_PROGRESS'
  | 'TRAINING:ENDORSE'
  | 'TRAINING:APPROVE_SOLO'

  // Availability permissions
  | 'AVAILABILITY:READ_OWN'
  | 'AVAILABILITY:UPDATE_OWN'
  | 'AVAILABILITY:DELETE_OWN'
  | 'AVAILABILITY:READ_ALL'
  | 'AVAILABILITY:UPDATE_OTHERS'
  | 'AVAILABILITY:DELETE_OTHERS'
  | 'AVAILABILITY:REQUEST_TIMEOFF'
  | 'AVAILABILITY:APPROVE_TIMEOFF'
  | 'AVAILABILITY:MANAGE_SYSTEM'

  // Organization permissions
  | 'ORG:READ'
  | 'ORG:UPDATE'
  | 'ORG:MANAGE_SETTINGS'
  | 'ORG:VIEW_ANALYTICS'

  // Document permissions
  | 'DOCUMENTS:READ'
  | 'DOCUMENTS:UPLOAD'
  | 'DOCUMENTS:VERIFY'
  | 'DOCUMENTS:DELETE';

/**
 * Low-level helper: does a given permission list include a permission.
 * Vertical matrices resolve to a `Permission[]`; use this to test membership.
 */
export function roleHasPermission(permissions: Permission[], permission: Permission): boolean {
  return permissions.includes(permission);
}

// Helper function to get role display name (short form for UI)
export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    admin: 'Admin',
    member: 'Member',
    cfi: 'CFI',
    student: 'Student',
    dispatch: 'Dispatch',
    maintenance: 'Maintenance'
  };
  return roleNames[role] || role;
}

// Helper function to get role description
export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    admin: 'Full administrative access to organization and all features',
    member: 'Standard pilot access for booking flights and managing profile',
    cfi: 'Can instruct students, approve flight plans, and manage training progress',
    student: 'Limited access for training flights and progress tracking',
    dispatch: 'Can coordinate flights, override conflicts, and manage scheduling',
    maintenance: 'Can manage aircraft maintenance, squawks, and work orders'
  };
  return descriptions[role] || 'Unknown role';
}
